import React, { forwardRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import * as S from "./ChatInterfaceStyles";
import { Attachment } from "./ChatInterface";

interface MessageBubbleProps {
  content: string;
  isUser: boolean;
  isLoading?: boolean;
  image_path?: string;
  attachments?: Attachment[];
  folder_info?: {
    name: string;
    file_count: number;
  };
}

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "bmp"];

const isImageAttachment = (att: Attachment): boolean => {
  if (!att.file_name) return false;
  const ext = att.file_name.split(".").pop()?.toLowerCase() || "";
  return IMAGE_EXTENSIONS.includes(ext) || (att.file_type?.startsWith("image/") ?? false);
};

const getFileIcon = (name: string): string => {
  if (!name) return "📎";
  const ext = name.split(".").pop()?.toLowerCase() || "";
  if (IMAGE_EXTENSIONS.includes(ext)) return "🖼️";
  if (ext === "pdf") return "📄";
  if (ext === "doc" || ext === "docx") return "📝";
  if (ext === "txt") return "📃";
  if (ext === "csv") return "📊";
  if (ext === "json") return "🔧";
  if (["mp3", "m4a", "wav", "aac", "ogg"].includes(ext)) return "🎵";
  if (["mp4", "mov", "webm"].includes(ext)) return "🎬";
  return "📎";
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const MessageBubble = forwardRef<HTMLDivElement, MessageBubbleProps>(({ content, isUser, isLoading, image_path, attachments, folder_info }, ref) => {
  const bgColor = isUser ? "var(--secondary-color)" : "transparent";
  const textColor = isUser ? "var(--primary-color)" : "var(--primary-color)";
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  let displayContent = content || "";
  if (isUser && displayContent) {
    const attachedFilesIndex = displayContent.indexOf("\n\nAttached files:\n");
    if (attachedFilesIndex !== -1) {
      displayContent = displayContent.substring(0, attachedFilesIndex);
    }
    const trimmedContent = displayContent.trim();
    if (
      trimmedContent.startsWith("Describe and summarize the uploaded file:") ||
      trimmedContent.startsWith("Describe and summarize these ") ||
      /Describe and summarize the uploaded file(s)?/i.test(trimmedContent)
    ) {
      displayContent = "";
    }
  }

  const imageAttachments = attachments?.filter(isImageAttachment) || [];
  const fileAttachments = attachments?.filter(a => !isImageAttachment(a)) || [];

  return (
    <>
      <div
        ref={ref}
        style={{
          display: "flex",
          justifyContent: isUser ? "flex-end" : "flex-start",
          margin: "8px 0",
          width: "100%",
          flexDirection: 'column',
          alignItems: isUser ? "flex-end" : "flex-start",
        }}
      >
        {image_path && (
          <div style={{ marginBottom: '8px' }}>
            <img
              src={image_path}
              alt="User uploaded"
              style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        )}

        {/* Attachment images */}
        {imageAttachments.length > 0 && (
          <S.MessageAttachmentGrid>
            {imageAttachments.map(att => (
              <div key={att.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? "flex-end" : "flex-start", marginBottom: '8px' }}>
                <S.MessageImagePreview
                  src={att.file_url}
                  alt={att.file_name}
                  onClick={() => setLightboxUrl(att.file_url)}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px', fontSize: '11px', color: '#64748b' }}>
                  <span style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={att.file_name}>
                    {att.file_name}
                  </span>
                  <a href={att.file_url} download={att.file_name} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: 500 }}>
                    Download
                  </a>
                </div>
              </div>
            ))}
          </S.MessageAttachmentGrid>
        )}

        {/* Message text */}
        {displayContent && (
          <div
            style={{
              background: bgColor,
              color: textColor,
              padding: isUser ? "10px 14px" : "2px 0",
              borderRadius: isUser ? "12px" : "0",
              maxWidth: isUser ? "70%" : "100%",
              border: "none",
              wordBreak: "break-word",
            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                a: (props: any) => {
                  const { children, ...rest } = props;
                  return (
                    <a {...rest} target="_blank" rel="noopener noreferrer" style={{ color: "#1a73e8", textDecoration: "underline" }}>
                      {children || rest.href || 'Link'}
                    </a>
                  );
                },
                code: ({ inline, className, children, ...props }: any) => (
                  <code
                    className={className}
                    style={{
                      backgroundColor: isUser ? "#2a2a40" : "#f5f5f5",
                      padding: inline ? "2px 4px" : "8px",
                      borderRadius: 4,
                      display: inline ? "inline" : "block",
                      overflowX: "auto",
                    }}
                    {...props}
                  >
                    {children}
                  </code>
                ),
                ul: (props: any) => <ul style={{ margin: "8px 0 8px 20px" }} {...props} />,
                ol: (props: any) => <ol style={{ margin: "8px 0 8px 20px" }} {...props} />,
                blockquote: (props: any) => (
                  <blockquote style={{ borderLeft: "3px solid #ccc", margin: 0, paddingLeft: 12, color: isUser ? "#ddd" : "#555" }} {...props} />
                ),
                table: (props: any) => (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ borderCollapse: "collapse", width: "100%" }} {...props} />
                  </div>
                ),
                th: (props: any) => <th style={{ border: "1px solid #ddd", padding: 6, background: isUser ? "#2a2a40" : "#f7f7f7" }} {...props} />,
                td: (props: any) => <td style={{ border: "1px solid #ddd", padding: 6 }} {...props} />,
              } as any}
            >
              {displayContent}
            </ReactMarkdown>
          </div>
        )}

        {/* Attachment file cards */}
        {fileAttachments.length > 0 && !folder_info && (
          <S.MessageAttachmentGrid>
            {fileAttachments.map(att => (
              <S.FileCardStyled
                key={att.id}
                href={att.file_url}
                target="_blank"
                rel="noopener noreferrer"
                download={att.file_name}
              >
                <S.FileCardIcon>{getFileIcon(att.file_name)}</S.FileCardIcon>
                <S.FileCardInfo>
                  <S.FileCardName>{att.file_name}</S.FileCardName>
                  <S.FileCardSize>{formatFileSize(att.file_size)}</S.FileCardSize>
                </S.FileCardInfo>
                <S.FileCardDownload>Download</S.FileCardDownload>
              </S.FileCardStyled>
            ))}
          </S.MessageAttachmentGrid>
        )}

        {/* Folder card */}
        {folder_info && (
          <S.MessageAttachmentGrid>
            <S.FileCardStyled
              as="div"
              style={{ cursor: 'default' }}
            >
              <S.FileCardIcon>📁</S.FileCardIcon>
              <S.FileCardInfo>
                <S.FileCardName>{folder_info.name}</S.FileCardName>
                <S.FileCardSize>{folder_info.file_count} files</S.FileCardSize>
              </S.FileCardInfo>
            </S.FileCardStyled>
          </S.MessageAttachmentGrid>
        )}
      </div>

      {/* Image lightbox */}
      {lightboxUrl && (
        <S.ImageLightbox onClick={() => setLightboxUrl(null)}>
          <S.LightboxImage src={lightboxUrl} alt="Full size preview" />
        </S.ImageLightbox>
      )}
    </>
  );
});

export default MessageBubble;
